import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useRole } from '../context/RoleContext';
import { api } from '../api/client';
import { useT } from '../i18n';
import type { SpeakrUserConfig, PermissionCapability } from '../types';

type ProfileTab = 'account' | 'permissions' | 'integrations' | 'notifications';
const PROFILE_TABS: ProfileTab[] = ['account', 'permissions', 'integrations', 'notifications'];

// Reads the active tab from the hash (#profile?tab=notifications) so a shared
// link can point straight at any tab, mirroring GerancePage's parseGeranceHash.
function parseProfileHash(): ProfileTab {
  const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
  const tab = params.get('tab');
  return (tab && (PROFILE_TABS as string[]).includes(tab)) ? tab as ProfileTab : 'account';
}

export function ProfilePage() {
  const { t } = useT();
  const { role, isAdmin, isDirection } = useRole();
  const [tab, setTab] = useState<ProfileTab>(() => parseProfileHash());

  // Keep the hash in sync with the open tab (replaceState, not pushState, so
  // switching tabs doesn't spam the back-stack) — the address bar is then
  // always a direct, shareable link back to the tab in view.
  useEffect(() => {
    const newHash = tab === 'account' ? '#profile' : `#profile?tab=${tab}`;
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, '', newHash);
    }
  }, [tab]);

  return (
    <div style={{ maxWidth: 640 }}>
      <div className="tabs" style={{ marginBottom: 'var(--s16)' }}>
        {PROFILE_TABS.map(tk => (
          <button key={tk} className={`tab${tab === tk ? ' active' : ''}`} onClick={() => setTab(tk)}>
            {t(`profile.tab.${tk}`)}
          </button>
        ))}
      </div>

      {tab === 'account' && <AccountTab />}
      {tab === 'permissions' && <PermissionsTab role={role} isAdmin={isAdmin} isDirection={isDirection} />}
      {tab === 'integrations' && <IntegrationsTab />}
      {tab === 'notifications' && <NotificationsTab role={role} isAdmin={isAdmin} isDirection={isDirection} />}
    </div>
  );
}

// ── Account Tab ──

function AccountTab() {
  const { t } = useT();
  const { user, authMethod } = useAuth();
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwStatus, setPwStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [pwError, setPwError] = useState('');

  const handlePasswordChange = async () => {
    setPwError('');
    if (newPw.length < 8) { setPwError(t('profile.pwMinLength')); return; }
    if (newPw !== confirmPw) { setPwError(t('profile.pwMismatch')); return; }
    setPwStatus('saving');
    try {
      await api.changePassword(currentPw, newPw);
      setPwStatus('saved');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      setTimeout(() => setPwStatus('idle'), 2000);
    } catch (err: any) {
      setPwStatus('error');
      setPwError(err.message || t('profile.pwChangeFailed'));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s24)' }}>
      <section>
        <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s12)' }}>{t('profile.accountTitle')}</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s8)' }}>
          <div className="detail-row">
            <span className="detail-row-label">{t('common.name')}</span>
            <span>{user?.name}</span>
          </div>
          <div className="detail-row">
            <span className="detail-row-label">{t('common.email')}</span>
            <span>{user?.email}</span>
          </div>
          <div className="detail-row">
            <span className="detail-row-label">{t('profile.role')}</span>
            {user?.isAdmin
              ? <span className="badge badge-accent">{t('profile.roleAdmin')}</span>
              : <span className="badge">{t('profile.roleUser')}</span>}
          </div>
          <div className="detail-row">
            <span className="detail-row-label">{t('profile.auth')}</span>
            <span className="text-ink-3" style={{ fontSize: 'var(--t-13)' }}>
              {authMethod === 'oidc' ? t('profile.authOidc') : t('profile.authPassword')}
            </span>
          </div>
        </div>
      </section>

      {authMethod === 'password' && (
        <section>
          <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s12)' }}>{t('profile.changePassword')}</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s12)' }}>
            <div className="form-group">
              <label className="form-label">{t('profile.currentPassword')}</label>
              <input className="form-input" type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} style={{ maxWidth: 320 }} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('profile.newPassword')}</label>
              <input className="form-input" type="password" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder={t('profile.pwPlaceholder')} style={{ maxWidth: 320 }} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('profile.confirmNewPassword')}</label>
              <input className="form-input" type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} style={{ maxWidth: 320 }} />
            </div>
            {pwError && <div style={{ color: 'var(--err)', fontSize: 'var(--t-13)' }}>{pwError}</div>}
            <div>
              <button
                className="btn btn-accent btn-sm"
                onClick={handlePasswordChange}
                disabled={!currentPw || !newPw || !confirmPw || pwStatus === 'saving'}
              >
                {pwStatus === 'saving' ? t('common.saving') : pwStatus === 'saved' ? t('profile.passwordChanged') : t('profile.changePassword')}
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

// ── Permissions Tab ──

function PermissionsTab({ role, isAdmin, isDirection }: { role: string; isAdmin: boolean; isDirection: boolean }) {
  const { t } = useT();
  const [capabilities, setCapabilities] = useState<PermissionCapability[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (api.getPermissionsOverview() as Promise<{ capabilities: PermissionCapability[] }>)
      .then(data => setCapabilities(data.capabilities))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const renderCell = (value: boolean | string) => {
    if (value === true) return <span style={{ color: 'var(--ok)' }}>{t('common.yes')}</span>;
    if (value === false) return <span style={{ color: 'var(--ink-3)' }}>{t('common.no')}</span>;
    return <span style={{ color: 'var(--warn)', fontSize: 'var(--t-12)' }}>{value}</span>;
  };

  // Filter columns based on role
  const showAdmin = isAdmin;
  const showDirection = isAdmin || isDirection;

  if (loading) return <p className="text-ink-3">{t('common.loading')}</p>;

  return (
    <div>
      <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s12)' }}>{t('profile.permissionsTitle')}</h2>
      <p className="text-sm text-ink-3" style={{ marginBottom: 'var(--s12)' }}>
        {t('profile.permissionsIntro', { role })}
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table className="data-table" style={{ width: '100%', fontSize: 'var(--t-13)' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>{t('profile.capability')}</th>
              {showAdmin && <th>{t('profile.roleAdmin')}</th>}
              {showDirection && <th>{t('profile.roleDirection')}</th>}
              <th>{t('profile.roleUser')}</th>
            </tr>
          </thead>
          <tbody>
            {capabilities.map((cap, i) => (
              <tr key={i}>
                <td>{cap.name}</td>
                {showAdmin && <td style={{ textAlign: 'center' }}>{renderCell(cap.admin)}</td>}
                {showDirection && <td style={{ textAlign: 'center' }}>{renderCell(cap.direction)}</td>}
                <td style={{ textAlign: 'center' }}>{renderCell(cap.user)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Integrations Tab ──

function IntegrationsTab() {
  const { t } = useT();
  const [calConfigured, setCalConfigured] = useState(false);
  const [speakrConfig, setSpeakrConfig] = useState<SpeakrUserConfig | null>(null);
  const [speakrToken, setSpeakrToken] = useState('');
  const [speakrUrl, setSpeakrUrl] = useState('');
  const [speakrSaved, setSpeakrSaved] = useState(false);
  const [speakrSyncing, setSpeakrSyncing] = useState(false);
  const [speakrSyncMsg, setSpeakrSyncMsg] = useState<string | null>(null);
  // My calendar sessions copied into the person's Outlook calendar (on by default).
  const [outlookSessions, setOutlookSessions] = useState(true);

  useEffect(() => {
    (api.getCalendarStatus() as Promise<{ configured: boolean }>)
      .then(s => setCalConfigured(s.configured)).catch(() => {});
    api.getProfile().then(p => setOutlookSessions(p.outlook_sessions !== false)).catch(() => {});
    (api.getSpeakrUserConfig() as Promise<SpeakrUserConfig>).then(cfg => {
      setSpeakrConfig(cfg);
      setSpeakrToken(cfg.hasToken ? cfg.tokenPreview : '');
      setSpeakrUrl(cfg.speakrBaseUrlOverride || '');
    }).catch(() => {});
  }, []);

  const handleSpeakrSave = async () => {
    const data: { speakr_api_token?: string; speakr_base_url?: string } = {};
    if (!speakrToken.startsWith('****')) { data.speakr_api_token = speakrToken; }
    data.speakr_base_url = speakrUrl;
    await api.saveSpeakrUserConfig(data);
    setSpeakrSaved(true);
    setTimeout(() => setSpeakrSaved(false), 2000);
    const cfg = await api.getSpeakrUserConfig() as SpeakrUserConfig;
    setSpeakrConfig(cfg);
    setSpeakrToken(cfg.hasToken ? cfg.tokenPreview : '');
    setSpeakrUrl(cfg.speakrBaseUrlOverride || '');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s24)' }}>
      {/* Calendar Sync */}
      <section>
        <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s12)' }}>{t('profile.calendarSync')}</h2>
        {!calConfigured ? (
          <p className="text-sm text-ink-3">{t('profile.calendarNotConfigured')}</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s8)' }}>
            <p className="text-sm text-ink-3">
              {t('profile.calendarInfoBefore')}<code>ae14</code>{t('profile.calendarInfoAfter')}
            </p>
            <span className="badge badge-ok" style={{ alignSelf: 'flex-start' }}>{t('profile.active')}</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s4)', marginTop: 'var(--s8)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={outlookSessions}
                  onChange={e => {
                    const enabled = e.target.checked;
                    setOutlookSessions(enabled);
                    api.updateProfileOutlookSessions(enabled).catch(() => setOutlookSessions(!enabled));
                  }}
                  style={{ width: 16, height: 16, accentColor: 'var(--accent)' }}
                />
                <span style={{ fontWeight: 500, fontSize: 'var(--t-14)' }}>{t('profile.outlookSessions')}</span>
              </label>
              <span className="text-ink-3" style={{ fontSize: 'var(--t-12)', marginLeft: 24 }}>{t('profile.outlookSessionsHelp')}</span>
            </div>
          </div>
        )}
      </section>

      {/* Speakr Integration */}
      <section>
        <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s12)' }}>{t('profile.speakrTitle')}</h2>
        {speakrConfig && !speakrConfig.speakrConfigured && !speakrConfig.speakrBaseUrl && (
          <p className="text-sm text-ink-3">{t('profile.speakrNotConfigured')}</p>
        )}
        {speakrConfig && (speakrConfig.speakrConfigured || speakrConfig.speakrBaseUrl) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s12)' }}>
            <p className="text-sm text-ink-3">
              {t('profile.speakrIntro')}
            </p>
            <div className="form-group">
              <label className="form-label">{t('profile.speakrUrl')}</label>
              <input
                className="form-input" value={speakrUrl}
                onChange={e => { setSpeakrUrl(e.target.value); setSpeakrSaved(false); }}
                placeholder={speakrConfig.speakrBaseUrl || 'http://192.168.80.214'}
                style={{ maxWidth: 360 }}
              />
              <p className="text-sm text-ink-3" style={{ marginTop: 4 }}>
                {t('profile.speakrUrlHint', { value: speakrConfig.speakrBaseUrl || t('profile.notSet') })}
              </p>
            </div>
            <div className="form-group">
              <label className="form-label">{t('profile.apiToken')}</label>
              <input
                className="form-input" value={speakrToken}
                onChange={e => { setSpeakrToken(e.target.value); setSpeakrSaved(false); }}
                onFocus={() => { if (speakrToken.startsWith('****')) setSpeakrToken(''); }}
                placeholder={t('profile.apiTokenPlaceholder')}
                style={{ maxWidth: 360 }}
              />
            </div>
            <div style={{ display: 'flex', gap: 'var(--s8)', alignItems: 'center' }}>
              <button className="btn btn-accent btn-sm" onClick={handleSpeakrSave}>
                {speakrSaved ? t('common.saved') : t('common.save')}
              </button>
              {speakrConfig.hasToken && <span className="badge badge-ok">{t('profile.tokenSet')}</span>}
              {speakrConfig.hasToken && (
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={speakrSyncing}
                  onClick={async () => {
                    setSpeakrSyncing(true); setSpeakrSyncMsg(null);
                    try {
                      const r = await api.triggerSpeakrSync() as { synced: number };
                      setSpeakrSyncMsg(r.synced === 1 ? t('profile.syncedOne', { n: r.synced }) : t('profile.syncedMany', { n: r.synced }));
                    } catch (e: any) {
                      setSpeakrSyncMsg(e?.message || t('profile.syncFailed'));
                    } finally {
                      setSpeakrSyncing(false);
                    }
                  }}
                >
                  {speakrSyncing ? t('profile.syncing') : t('profile.syncNow')}
                </button>
              )}
              {speakrConfig.hasToken && (
                <button
                  className="btn btn-ghost btn-sm text-err"
                  onClick={async () => {
                    await api.saveSpeakrUserConfig({ speakr_api_token: '' });
                    const cfg = await api.getSpeakrUserConfig() as SpeakrUserConfig;
                    setSpeakrConfig(cfg);
                    setSpeakrToken('');
                  }}
                >
                  {t('profile.clearToken')}
                </button>
              )}
              {speakrSyncMsg && <span className="text-sm text-ink-3">{speakrSyncMsg}</span>}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

// ── Notifications Tab ──

// Translated label/description per preference type (the server sends English
// defaults, kept as fallback for types not listed here). Static keys so the
// i18n checker can resolve them.
const NOTIF_TYPE_KEYS: Record<string, { label: string; desc: string }> = {
  mention: { label: 'profile.notifType.mention', desc: 'profile.notifType.mention.desc' },
  task_contact: { label: 'profile.notifType.task_contact', desc: 'profile.notifType.task_contact.desc' },
  email_task_contact: { label: 'profile.notifType.email_task_contact', desc: 'profile.notifType.email_task_contact.desc' },
  email_task_assigned: { label: 'profile.notifType.email_task_assigned', desc: 'profile.notifType.email_task_assigned.desc' },
  email_project_involvement: { label: 'profile.notifType.email_project_involvement', desc: 'profile.notifType.email_project_involvement.desc' },
  project_status_changed: { label: 'profile.notifType.project_status_changed', desc: 'profile.notifType.project_status_changed.desc' },
  project_description_changed: { label: 'profile.notifType.project_description_changed', desc: 'profile.notifType.project_description_changed.desc' },
  task_created_in_project: { label: 'profile.notifType.task_created_in_project', desc: 'profile.notifType.task_created_in_project.desc' },
  task_status_changed_in_project: { label: 'profile.notifType.task_status_changed_in_project', desc: 'profile.notifType.task_status_changed_in_project.desc' },
  project_comment: { label: 'profile.notifType.project_comment', desc: 'profile.notifType.project_comment.desc' },
};

function NotificationsTab({ isAdmin, isDirection }: { role: string; isAdmin: boolean; isDirection: boolean }) {
  const { t } = useT();
  const [prefs, setPrefs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (api.getNotificationPreferences() as Promise<any[]>)
      .then(setPrefs)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const togglePref = async (type: string, enabled: boolean, subOption?: string) => {
    await api.updateNotificationPreference(type, enabled, subOption);
    setPrefs(prev => prev.map(p =>
      p.notification_type === type ? { ...p, enabled, sub_option: subOption ?? p.sub_option } : p
    ));
  };

  if (loading) return <p className="text-ink-3">{t('common.loading')}</p>;

  const showSponsorTypes = isAdmin || isDirection;

  return (
    <div>
      <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s12)' }}>{t('profile.notifPrefsTitle')}</h2>
      <p className="text-sm text-ink-3" style={{ marginBottom: 'var(--s16)' }}>
        {t('profile.notifPrefsIntro')}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s16)' }}>
        {prefs
          .filter(p => !p.sponsor_only || showSponsorTypes)
          .map(p => (
          <div key={p.notification_type} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s4)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={p.enabled}
                onChange={e => togglePref(p.notification_type, e.target.checked)}
                style={{ width: 16, height: 16, accentColor: 'var(--accent)' }}
              />
              <span style={{ fontWeight: 500, fontSize: 'var(--t-14)' }}>{NOTIF_TYPE_KEYS[p.notification_type] ? t(NOTIF_TYPE_KEYS[p.notification_type].label) : p.label}</span>
            </label>
            <span className="text-ink-3" style={{ fontSize: 'var(--t-12)', marginLeft: 24 }}>{NOTIF_TYPE_KEYS[p.notification_type] ? t(NOTIF_TYPE_KEYS[p.notification_type].desc) : p.description}</span>
            {p.has_sub_option && p.enabled && (
              <div style={{ marginLeft: 24, marginTop: 'var(--s4)', display: 'flex', flexDirection: 'column', gap: 'var(--s4)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', cursor: 'pointer', fontSize: 'var(--t-13)' }}>
                  <input
                    type="radio"
                    name={`sub_${p.notification_type}`}
                    checked={!p.sub_option || p.sub_option === 'all'}
                    onChange={() => togglePref(p.notification_type, true, 'all')}
                  />
                  {t('profile.notifAllProjects')}
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', cursor: 'pointer', fontSize: 'var(--t-13)' }}>
                  <input
                    type="radio"
                    name={`sub_${p.notification_type}`}
                    checked={p.sub_option === 'sponsored_only'}
                    onChange={() => togglePref(p.notification_type, true, 'sponsored_only')}
                  />
                  {t('profile.notifSponsoredOnly')}
                </label>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
